import { z } from 'zod/v3';
import { err, ok, Result } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import { GraphModel } from '../../graph/graph.model';
import { CsvUploadModal } from './csv-upload.view';
import { ModalItem } from '@cotera/client/app/components/app';
import { AskResponse } from '../../actions/framework/types';
import { DATASET_ACTION_GROUP } from '../actions/constants';
import { deserialize, MAX_CSV_DATA_ROWS } from './csv-upload.view';
import { Ty } from '@cotera/era';
import { parse } from 'csv-parse';
import { Assert } from '@cotera/utilities';
import { CsvUploadModel } from './csv-upload.model';
import { ConfigureTableAction } from './configure-table.action';

export type CsvUploadParams = {};

export type CsvUploadResult = {
  headerRow: string[];
  dataRows: Ty.Scalar[][];
};

export type CsvUploadError =
  | { t: 'empty-csv-file' }
  | { t: 'exceeded-max-rows'; message: string }
  | { t: 'parse-error'; message: string }
  | { t: 'create-table-error' };

type ModalManager = {
  pop: () => void;
  push: (
    child: ModalItem & {
      onClose?: () => void;
    }
  ) => void;
};

export class CsvUploadAction extends BaseAction<
  CsvUploadParams,
  CsvUploadError[]
> {
  title = 'Upload CSV';
  description = 'Create a new dataset from a CSV file';
  inputSchema = z.object({});
  group = DATASET_ACTION_GROUP;
  icon = 'upload';

  constructor(private readonly modalManager: ModalManager) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.isInScope(GraphModel) && context.isInScope(CsvUploadModel);
  }

  async execute(
    _: CsvUploadParams,
    context: ExecuteContext
  ): Promise<ActionResult<CsvUploadError[]>> {
    const model = context.getInScopeModelOfType(CsvUploadModel);
    const graphModel = context.getInScopeModelOfType(GraphModel);

    Assert.assert(graphModel !== null, 'GraphModel not found');
    Assert.assert(model !== null, 'CsvUploadModel not found');

    model.state.set({
      t: 'parsing-file',
      file: model.tentativeFile.snapshot()!,
    });

    const state = model.state.snapshot();

    Assert.assert(
      state.t === 'parsing-file',
      'CsvUploadModel not in parsing file state'
    );

    const parseResult = await this.parseFileContent(
      model.tentativeFile.snapshot()!
    );

    if (parseResult.isErr()) {
      model.state.set({
        t: 'await-upload',
        errors: parseResult.error.map((e) => ({ t: e.t })),
      });

      //return ok so that the modal stays open
      return ok({
        next: true,
      });
    }

    model.headerRow.set(parseResult.value.headerRow);
    model.dataRows.set(parseResult.value.dataRows);

    model.state.set({
      t: 'await-upload',
    });

    await context.getAction(ConfigureTableAction).askOrExecute({}, context);

    return ok({});
  }

  /**
   * Converts an uploaded CSV file into a list of list of strings,
   * where each top-level list represents a row in the CSV file
   * while each sub-list contains the entries of the row.
   * Returns an error if the file is empty, exceeds the maximum number of rows, or if there is a parse error.
   */
  private async parseFileContent(
    file: File
  ): Promise<Result<CsvUploadResult, CsvUploadError[]>> {
    try {
      const text = await file.text();

      const rows = await new Promise<string[][]>((resolve, reject) => {
        const parser = parse({
          relax_column_count: true,
          skip_empty_lines: false,
          quote: '"',
          escape: '"',
        });

        const rows: string[][] = [];

        parser.on('readable', () => {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const line: string[] | null = parser.read();

            if (line === null) {
              break;
            }

            rows.push(line);
          }
        });

        parser.on('error', (e) => {
          reject(e);
        });

        parser.on('end', () => {
          resolve(rows);
        });

        parser.write(text);

        parser.end();
      });

      const headerRow = rows[0];

      if (headerRow === undefined) {
        return err([{ t: 'empty-csv-file' }]);
      }

      const columnNames = deduplicateHeaders(sanitizeHeaderRow(headerRow));

      const rawDataRows = rows.slice(1);

      if (rawDataRows.length > MAX_CSV_DATA_ROWS) {
        return err([
          {
            t: 'exceeded-max-rows',
            message: `CSV file exceeds maximum supported rows (${MAX_CSV_DATA_ROWS}).`,
          },
        ]);
      }

      const dataRows = deserialize(rawDataRows);

      return ok({ headerRow: columnNames, dataRows });
    } catch (e) {
      return err([
        {
          t: 'parse-error',
          message: e instanceof Error ? e.message : 'Unknown parse error',
        },
      ]);
    }
  }

  shouldAsk(_context: ApplicableContext): boolean {
    return true;
  }

  ask(
    _: Partial<CsvUploadParams>,
    _context: ExecuteContext
  ): AskResponse<CsvUploadParams> {
    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <CsvUploadModal
              onContinue={async () => {
                await onRespond({});
              }}
            />
          ),
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

export const sanitizeHeaderRow = (headerRow: string[]): string[] => {
  let emptyCount = 0;

  return headerRow.map((header) => {
    if (header === '') {
      return `(empty header ${emptyCount++ + 1})`;
    }
    return header.trim();
  });
};

export const deduplicateHeaders = (headers: string[]): string[] => {
  const seen = new Map<string, number>();
  return headers.map((header) => {
    const count = seen.get(header) ?? 0;
    seen.set(header, count + 1);
    if (count === 0) {
      return header;
    }
    let suffix = count + 1;
    while (seen.has(`${header}_${suffix}`)) {
      suffix++;
    }
    const renamed = `${header}_${suffix}`;
    seen.set(renamed, 1);
    return renamed;
  });
};
